'use client';
import React, { useState, useMemo, useEffect, useRef } from 'react';
import Link from 'next/link';
import { MapPin, Car, ChevronDown, ChevronUp, ArrowRight, Home } from 'lucide-react';
import { SCHOOLS } from '@/data/schools';
import { COMMUNITY_PRICES, formatCommuteTime } from '@/data/communityPrices';
import { toSlug } from '@/lib/seo/slugs';
import KhdaBadge from '@/components/ui/KhdaBadge';
import CurriculumBadge from '@/components/ui/CurriculumBadge';
import type { KhdaRating } from '@/components/ui/KhdaBadge';
import type { Curriculum } from '@/components/ui/CurriculumBadge';
import { trackCommunityProfileView } from '@/lib/analytics/ga4Events';

interface SchoolCommute {
  id: string;
  name: string;
  travelMins: number;
}

interface Props {
  communityName: string;
  communitySlug: string;
  schoolsWithCommute: SchoolCommute[];
}

const CURRICULA: Curriculum[] = ['British', 'American', 'IB', 'Indian CBSE', 'Indian ICSE', 'French', 'MOE Arabic', 'Other'];
const KHDA_RATINGS: KhdaRating[] = ['Outstanding', 'Very Good', 'Good', 'Acceptable', 'Weak', 'Very Weak', 'Not yet inspected'];
const COMMUTE_OPTIONS = [10, 15, 20, 25, 30];
const FEE_BANDS = [
  { label: 'Any', min: 0, max: 999999 },
  { label: 'Under AED 20K', min: 0, max: 20000 },
  { label: 'AED 20K–40K', min: 20000, max: 40000 },
  { label: 'AED 40K–70K', min: 40000, max: 70000 },
  { label: 'AED 70K+', min: 70000, max: 999999 },
];

function commuteColor(mins: number): string {
  if (mins <= 10) return '#16a34a';
  if (mins <= 20) return '#2563eb';
  if (mins <= 30) return '#d97706';
  return '#dc2626';
}

function formatFee(n: number) {
  if (n >= 1000) return `AED ${(n / 1000).toFixed(0)}K`;
  return `AED ${n}`;
}

export default function SchoolsNearClient({ communityName, communitySlug, schoolsWithCommute }: Props) {
  const [maxCommute, setMaxCommute] = useState(30);
  const [selectedCurricula, setSelectedCurricula] = useState<Curriculum[]>([]);
  const [selectedRatings, setSelectedRatings] = useState<KhdaRating[]>([]);
  const [feeBand, setFeeBand] = useState(0);
  const [showFilters, setShowFilters] = useState(false);

  // Fire community_profile_view once per community page load
  const profileViewFiredRef = useRef<string>('');
  useEffect(() => {
    if (profileViewFiredRef.current === communitySlug) return;
    profileViewFiredRef.current = communitySlug;
    trackCommunityProfileView({ community_name: communityName, community_slug: communitySlug });
  }, [communityName, communitySlug]);

  // Raw commute map — used for threshold filtering (unrounded)
  const commuteMapRaw = useMemo(() => {
    const map: Record<string, number> = {};
    schoolsWithCommute.forEach(s => { map[s.id] = s.travelMins; });
    return map;
  }, [schoolsWithCommute]);

  // Display commute map — rounded to nearest 5 min for display only
  const commuteMapDisplay = useMemo(() => {
    const map: Record<string, number> = {};
    schoolsWithCommute.forEach(s => { map[s.id] = formatCommuteTime(s.travelMins); });
    return map;
  }, [schoolsWithCommute]);

  const filtered = useMemo(() => {
    const feeBandObj = FEE_BANDS[feeBand];
    return SCHOOLS
      .filter(school => {
        // Use RAW value for threshold comparison — prevents 32-min journey passing ≤30 filter
        const rawMins = commuteMapRaw[school.id] ?? 999;
        if (rawMins > maxCommute) return false;
        if (selectedCurricula.length > 0 && !selectedCurricula.includes(school.curriculum as Curriculum)) return false;
        if (selectedRatings.length > 0 && !selectedRatings.includes(school.khdaRating as KhdaRating)) return false;
        if (school.feeRangeMin < feeBandObj.min || school.feeRangeMin > feeBandObj.max) return false;
        return true;
      })
      .sort((a, b) => (commuteMapRaw[a.id] ?? 999) - (commuteMapRaw[b.id] ?? 999));
  }, [commuteMapRaw, maxCommute, selectedCurricula, selectedRatings, feeBand]);

  const curriculumCounts = useMemo(() => {
    const counts: Record<Curriculum, number> = {} as Record<Curriculum, number>;
    CURRICULA.forEach(c => {
      counts[c] = SCHOOLS.filter(s => {
        const rawMins = commuteMapRaw[s.id] ?? 999;
        return rawMins <= 30 && s.curriculum === c;
      }).length;
    });
    return counts;
  }, [commuteMapRaw]);

  function toggleCurriculum(c: Curriculum) {
    setSelectedCurricula(prev => prev.includes(c) ? prev.filter(x => x !== c) : [...prev, c]);
  }
  function toggleRating(r: KhdaRating) {
    setSelectedRatings(prev => prev.includes(r) ? prev.filter(x => x !== r) : [...prev, r]);
  }

  const searchUrl = `/school-search-discovery?community=${encodeURIComponent(communityName)}&maxCommute=${maxCommute}`;

  const relatedCommunities = COMMUNITY_PRICES
    .filter(c => c.name !== communityName)
    .sort((a, b) => {
      const distA = Math.sqrt(Math.pow(a.lat - COMMUNITY_PRICES.find(x => x.name === communityName)!.lat, 2) + Math.pow(a.lng - COMMUNITY_PRICES.find(x => x.name === communityName)!.lng, 2));
      const distB = Math.sqrt(Math.pow(b.lat - COMMUNITY_PRICES.find(x => x.name === communityName)!.lat, 2) + Math.pow(b.lng - COMMUNITY_PRICES.find(x => x.name === communityName)!.lng, 2));
      return distA - distB;
    })
    .slice(0, 5);

  return (
    <main className="w-full max-w-5xl mx-auto px-3 sm:px-4 lg:px-6 py-6 pb-20 overflow-x-hidden">
      <nav className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground mb-4" aria-label="Breadcrumb">
        <Link href="/" className="hover:text-primary flex items-center gap-1 flex-shrink-0"><Home size={12} />Home</Link>
        <span className="flex-shrink-0">/</span>
        <Link href="/schools-near" className="hover:text-primary flex-shrink-0">Schools by Area</Link>
        <span className="flex-shrink-0">/</span>
        <span className="text-foreground font-medium truncate">Schools near {communityName}</span>
      </nav>

      <header className="mb-6">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground mb-3 leading-tight">
          Schools Near {communityName}, Dubai
        </h1>
        <p className="text-sm sm:text-base text-muted-foreground mb-4 leading-relaxed max-w-2xl">
          Find and compare Dubai schools near {communityName}. Compare realistic commute times, KHDA ratings, curriculum, fees and admissions availability using SchoolMapper.
        </p>
        <div className="inline-flex items-center gap-2 bg-primary/8 border border-primary/20 rounded-lg px-3 py-1.5 text-xs sm:text-sm font-medium text-primary flex-wrap">
          <MapPin size={14} className="flex-shrink-0" />
          <span className="truncate">{communityName}</span>
          <ArrowRight size={14} className="flex-shrink-0" />
          <span className="flex-shrink-0">Nearby Schools</span>
        </div>
      </header>

      <section className="bg-white border border-border rounded-xl mb-6 overflow-hidden">
        <button
          onClick={() => setShowFilters(v => !v)}
          className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-foreground hover:bg-muted/30 transition-colors"
          aria-expanded={showFilters}
        >
          <span>Filter Schools</span>
          {showFilters ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>

        {showFilters && (
          <div className="px-4 pb-4 border-t border-border space-y-4 pt-4 overflow-x-hidden">
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Maximum Commute</p>
              <div className="flex flex-wrap gap-2">
                {COMMUTE_OPTIONS.map(m => (
                  <button
                    key={m}
                    onClick={() => setMaxCommute(m)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors flex-shrink-0 ${maxCommute === m ? 'bg-primary text-white border-primary' : 'bg-white text-foreground border-border hover:border-primary/50'}`}
                  >
                    ≤ {m} min
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Curriculum</p>
              <div className="flex flex-wrap gap-2">
                {CURRICULA.map(c => (
                  <button
                    key={c}
                    onClick={() => toggleCurriculum(c)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors flex-shrink-0 ${selectedCurricula.includes(c) ? 'bg-primary text-white border-primary' : 'bg-white text-foreground border-border hover:border-primary/50'}`}
                  >
                    {c} {curriculumCounts[c] > 0 && `(${curriculumCounts[c]})`}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">KHDA Rating</p>
              <div className="flex flex-wrap gap-2">
                {KHDA_RATINGS.map(r => (
                  <button
                    key={r}
                    onClick={() => toggleRating(r)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors flex-shrink-0 ${selectedRatings.includes(r) ? 'bg-primary text-white border-primary' : 'bg-white text-foreground border-border hover:border-primary/50'}`}
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Fee Range</p>
              <div className="flex flex-wrap gap-2">
                {FEE_BANDS.map((b, i) => (
                  <button
                    key={b.label}
                    onClick={() => setFeeBand(i)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors flex-shrink-0 ${feeBand === i ? 'bg-primary text-white border-primary' : 'bg-white text-foreground border-border hover:border-primary/50'}`}
                  >
                    {b.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </section>

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 mb-4">
        <p className="text-sm font-semibold text-foreground">
          {filtered.length} school{filtered.length !== 1 ? 's' : ''} within {maxCommute} min of {communityName}
        </p>
        <Link href={searchUrl} className="text-xs text-primary hover:underline font-medium whitespace-nowrap">
          Open in full search →
        </Link>
      </div>

      <div className="space-y-3 mb-8">
        {filtered.length === 0 && (
          <div className="text-center py-12 text-muted-foreground text-sm">
            No schools match the current filters. Try increasing the commute time or removing filters.
          </div>
        )}
        {filtered.map(school => {
          // Use display value (rounded) for showing to user
          const displayMins = commuteMapDisplay[school.id] ?? 999;
          const rawMins = commuteMapRaw[school.id] ?? 999;
          const schoolSlug = toSlug(school.name);
          return (
            <article key={school.id} className="bg-white border border-border rounded-xl p-4 hover:shadow-md transition-shadow overflow-hidden">
              <div className="flex flex-col sm:flex-row items-start justify-between gap-3 min-w-0">
                <div className="flex-1 min-w-0 w-full">
                  <div className="flex items-start gap-2 flex-wrap mb-1">
                    <Link href={`/school-profile-comparison?school=${school.id}`} className="text-base font-semibold text-foreground hover:text-primary transition-colors leading-tight break-words">
                      {school.name}
                    </Link>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground mb-2">
                    <span className="flex items-center gap-1 flex-shrink-0"><MapPin size={11} />{school.location}</span>
                    <span className="flex-shrink-0">{school.ageRange}</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <KhdaBadge rating={school.khdaRating} size="sm" />
                    <CurriculumBadge curriculum={school.curriculum} size="sm" />
                  </div>
                  <div className="text-xs text-muted-foreground break-words">
                    Fees: {formatFee(school.feeRangeMin)} – {formatFee(school.feeRangeMax)}
                  </div>
                </div>

                <div className="flex-shrink-0 flex flex-col items-end gap-2 w-full sm:w-auto">
                  <div className="flex items-center gap-1 px-2.5 py-1 rounded-full text-white text-xs font-bold whitespace-nowrap" style={{ backgroundColor: commuteColor(rawMins) }}>
                    <Car size={11} />
                    ~{displayMins} min
                  </div>
                  <Link href={`/communities-near/${schoolSlug}`} className="text-xs text-primary hover:underline whitespace-nowrap">
                    Communities nearby →
                  </Link>
                </div>
              </div>

              <div className="mt-3 pt-3 border-t border-border/50 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                <Link href={`/school-profile-comparison?school=${school.id}`} className="text-xs font-medium text-primary hover:underline flex items-center gap-1 flex-shrink-0">
                  View school profile <ArrowRight size={11} />
                </Link>
              </div>
            </article>
          );
        })}
      </div>

      <section className="bg-muted/30 border border-border rounded-xl p-5 mb-8">
        <h2 className="text-lg font-semibold text-foreground mb-3">
          Finding a school near {communityName}
        </h2>
        <p className="text-sm text-muted-foreground leading-relaxed">
          Choosing a school involves more than distance alone. Parents in {communityName} can use SchoolMapper to compare schools based on realistic commute times from their home, school-run practicality, curriculum options, KHDA inspection ratings, annual fees and admissions availability. The right school balances academic quality, location convenience and family values.
        </p>
      </section>

      {filtered.length > 0 && (
        <section className="mb-8">
          <h2 className="text-lg font-semibold text-foreground mb-4">
            Schools near {communityName} by curriculum
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              { label: 'British Schools', curriculum: 'British' },
              { label: 'IB Schools', curriculum: 'IB' },
              { label: 'American Schools', curriculum: 'American' },
              { label: 'CBSE Schools', curriculum: 'Indian CBSE' },
            ].map(item => {
              const count = SCHOOLS.filter(s => {
                const rawMins = commuteMapRaw[s.id] ?? 999;
                return rawMins <= 30 && s.curriculum === item.curriculum;
              }).length;
              return count > 0 ? (
                <Link key={item.curriculum} href={`/school-search-discovery?community=${encodeURIComponent(communityName)}&curriculum=${encodeURIComponent(item.curriculum)}`} className="flex items-center justify-between p-4 bg-white border border-border rounded-lg hover:border-primary hover:shadow-md transition-all">
                  <span className="text-sm font-medium text-foreground">{item.label}</span>
                  <span className="text-xs font-semibold text-primary bg-primary/10 px-2.5 py-1 rounded-full">{count}</span>
                </Link>
              ) : null;
            })}
          </div>
        </section>
      )}

      <div className="flex flex-col sm:flex-row gap-3 mb-8">
        <Link href={searchUrl} className="flex-1 flex items-center justify-center gap-2 bg-primary text-white rounded-xl px-5 py-3.5 text-sm font-semibold hover:bg-primary/90 transition-colors whitespace-nowrap">
          Explore All Schools Near {communityName}
          <ArrowRight size={16} className="flex-shrink-0" />
        </Link>
        <Link href="/school-search-discovery" className="flex items-center justify-center gap-2 border border-border rounded-xl px-5 py-3.5 text-sm font-medium text-foreground hover:bg-muted/30 transition-colors whitespace-nowrap">
          Change Community
        </Link>
      </div>

      {relatedCommunities.length > 0 && (
        <section className="mb-8">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
            Explore nearby communities
          </h2>
          <div className="flex flex-wrap gap-2">
            {relatedCommunities.map(c => (
              <Link key={c.name} href={`/schools-near/${toSlug(c.name)}`} className="text-xs px-3 py-1.5 rounded-full border border-border hover:border-primary/50 hover:text-primary transition-colors text-muted-foreground flex-shrink-0">
                {c.name}
              </Link>
            ))}
          </div>
        </section>
      )}

      <section className="mt-10 pt-6 border-t border-border">
        <p className="text-xs text-muted-foreground mb-3">
          <Link href="/school-search-discovery" className="text-primary hover:underline font-medium">
            Search all Dubai schools
          </Link>
          {' '}or explore{' '}
          <Link href="/communities-near" className="text-primary hover:underline font-medium">
            communities near a school
          </Link>
        </p>
      </section>
    </main>
  );
}
